import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Property, Tenant } from '../../types/properties';
import toast from 'react-hot-toast';

interface PaymentFormProps {
  properties: Property[];
  tenants: Tenant[];
  onClose: () => void;
  onSuccess: () => void;
}

export function PaymentForm({ properties, tenants, onClose, onSuccess }: PaymentFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    property_id: '',
    tenant_id: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'online',
    status: 'paid',
    notes: ''
  });

  // Auto-select tenant when property is selected
  const handlePropertyChange = (propertyId: string) => {
    const activeTenant = tenants.find(t => t.property_id === propertyId && t.status === 'active');
    setFormData(prev => ({
      ...prev,
      property_id: propertyId,
      tenant_id: activeTenant ? activeTenant.id : '',
      amount: activeTenant?.monthly_rent ? activeTenant.monthly_rent.toString() : ''
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.property_id || !formData.amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.from('rent_payments').insert({
        property_id: formData.property_id,
        tenant_id: formData.tenant_id || null,
        amount: parseFloat(formData.amount),
        payment_date: formData.payment_date,
        payment_method: formData.payment_method,
        status: formData.status,
        notes: formData.notes
      });

      if (error) throw error;

      toast.success('Payment recorded successfully');
      onSuccess();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const occupiedProperties = properties.filter(p => p.status === 'occupied');

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Property</label>
          <select
            required
            value={formData.property_id}
            onChange={e => handlePropertyChange(e.target.value)}
            className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-white focus:border-[var(--t-primary)] outline-none"
          >
            <option value="">-- Select Property --</option>
            {occupiedProperties.map(p => (
              <option key={p.id} value={p.id}>{p.address}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Tenant</label>
          <select
            value={formData.tenant_id}
            onChange={e => setFormData({ ...formData, tenant_id: e.target.value })}
            className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-white focus:border-[var(--t-primary)] outline-none"
          >
            <option value="">-- Select Tenant (Optional) --</option>
            {tenants.filter(t => t.property_id === formData.property_id).map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]">$</span>
              <input required type="number" step="0.01" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl pl-8 pr-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Date</label>
            <input required type="date" value={formData.payment_date} onChange={e => setFormData({ ...formData, payment_date: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Method</label>
            <select value={formData.payment_method} onChange={e => setFormData({ ...formData, payment_method: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-white focus:border-[var(--t-primary)] outline-none">
              <option value="online">Online / ACH</option>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="bank-transfer">Bank Transfer</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Status</label>
            <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2.5 text-white focus:border-[var(--t-primary)] outline-none">
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="late">Late</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[var(--t-text-muted)] mb-1">Notes (Optional)</label>
          <input type="text" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Check #, reference #, etc." className="w-full bg-[var(--t-background)] border border-[var(--t-border)] rounded-xl px-4 py-2 text-white focus:border-[var(--t-primary)] outline-none" />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[var(--t-border)]">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-[var(--t-text-muted)] hover:text-white transition-colors">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-[var(--t-primary)] text-white text-sm font-bold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors disabled:opacity-50">
          {isSubmitting ? 'Saving...' : 'Record Payment'}
        </button>
      </div>
    </form>
  );
}
